import { ActivatedRouteSnapshot, Route } from '@angular/router';
import { UserGroup } from '@core/api/angular';
import { featureFlagGuard, IFeatureFlagKeys } from '@lassi/config';
import { unsavedChangesGuard } from '@lassi/webapp-component';
import {
  AdminRoles,
  advancedAddressSearchFormDataResolver,
  AuthSteps,
  baseLayerGuard,
  customEnquiryOption,
  enquiryOptions,
  IdentifyFeatureType,
  presetGuard,
  RouteUtilsService,
  searchAddressForLocalityGuard,
} from '@lassi/webapp-shared';
import { TourMatMenuModule } from 'ngx-ui-tour-md-menu';
import {
  authGuard,
  authStepRedirectFn,
  logoutRedirectFn,
  nonLoggedInGuard,
  smesSubmissionRedirectFn,
  userRoleGuard,
} from './auth/auth';
import {
  clearSavedPath,
  restoreCurrentPath,
  saveCurrentPath,
} from './map/menu/menu.guard';
import { identifyRedirect } from './map/route';
import { goBackFromEditMark } from './shared/resolvers';
import { isPMMarkGuard } from './smes/pm-mark.guard';
import { polygonSearchGuard } from './vicnames/polygon/polygon-search.guard';
import { polygonSearchResolver } from './vicnames/polygon/polygon-search.resolver';

const wideDrawer = {
  drawerSize: 'calc(100vw - 79px - 2rem)',
  drawerMode: 'over',
};

const commonRoutes: Route[] = [
  {
    path: 'exports',
    canActivate: [featureFlagGuard(IFeatureFlagKeys.enableExports)],
    canDeactivate: [clearSavedPath],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./exports/exports-home.component').then(
            (c) => c.ExportsHomeComponent,
          ),
        canActivate: [restoreCurrentPath],
        data: {
          title: 'Export',
        },
      },
      {
        path: 'bulk-download',
        loadComponent: () =>
          import('./exports/bulk-download.component').then(
            (c) => c.BulkDownloadComponent,
          ),
        canDeactivate: [saveCurrentPath()],
        data: {
          drawerBack: 'exports',
          title: 'Bulk download',
        },
      },
      {
        path: 'print-map',
        loadComponent: () =>
          import('./exports/map-print.component').then(
            (c) => c.MapPrintComponent,
          ),
        canDeactivate: [saveCurrentPath()],
        data: {
          drawerBack: 'exports',
          title: 'Print map options',
        },
      },
    ],
  },
  {
    path: 'survey-label',
    runGuardsAndResolvers: 'always',
    canActivate: [userRoleGuard([UserGroup.DQA_ADMIN, UserGroup.LASSI_ADMIN])],
    loadComponent: () =>
      import('./survey-label/survey-label-home.component').then(
        (c) => c.SurveyLabelHomeComponent,
      ),
    data: {
      title: 'Survey Labels',
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./survey-label/survey-label-none-selected.component').then(
            (c) => c.SurveyLabelNoneSelectedComponent,
          ),
      },
      {
        path: 'add',
        loadComponent: () =>
          import('./survey-label/add-survey-label.component').then(
            (c) => c.AddSurveyLabelComponent,
          ),
        canDeactivate: [unsavedChangesGuard],
        data: {
          title: 'Add a new survey label',
          drawerBack: 'survey-label',
        },
      },
      {
        path: 'edit/:pfi',
        loadComponent: () =>
          import('./survey-label/edit-survey-label.component').then(
            (c) => c.EditSurveyLabelComponent,
          ),
        canDeactivate: [unsavedChangesGuard],
        data: {
          title: 'Manage an existing survey label',
          drawerBack: 'survey-label',
        },
      },
      {
        path: 'manage',
        redirectTo: 'manage/',
        pathMatch: 'full',
      },
      {
        path: 'manage/:latlng',
        loadComponent: () =>
          import('./survey-label/manage-survey-label.component').then(
            (c) => c.ManageSurveyLabelComponent,
          ),
        data: {
          title: 'Manage an existing survey label',
          drawerBack: 'survey-label',
        },
      },
    ],
  },
  {
    path: 'survey-mark-select',
    runGuardsAndResolvers: 'always',
    loadComponent: () =>
      import('./survey-mark-list/survey-mark-select.component').then(
        (c) => c.SurveyMarkSelectComponent,
      ),
    data: {
      title: 'Survey Mark selection',
      bottomSheetBack: '../landing',
    },
  },
  {
    path: 'survey-mark-list',
    runGuardsAndResolvers: 'always',
    loadComponent: () =>
      import('./survey-mark-list/survey-mark-list.component').then(
        (c) => c.SurveyMarkListComponent,
      ),
    data: {
      title: 'Survey Marks List',
      bottomSheetBack: '../landing',
      ...wideDrawer,
    },
  },
  {
    path: 'vicnames',
    canDeactivate: [clearSavedPath],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./vicnames/vicnames-home.component').then(
            (c) => c.VicnamesHomeComponent,
          ),
        canActivate: [restoreCurrentPath],
        data: {
          title: 'VICNAMES',
          bottomSheetBack: '../landing',
        },
      },
      {
        path: 'polygon-search',
        loadComponent: () =>
          import('./vicnames/polygon/polygon-search-result.component').then(
            (c) => c.PolygonSearchResultComponent,
          ),
        canActivate: [polygonSearchGuard],
        children: [
          {
            path: 'feature',
            loadComponent: () =>
              import(
                './vicnames/polygon/polygon-search-feature-result.component'
              ).then((c) => c.PolygonSearchFeatureResultComponent),
            resolve: {
              geoJson: polygonSearchResolver,
            },
            data: {
              title: 'Polygon search results',
              drawerBack: 'vicnames',
              bottomSheetBack: 'vicnames',
              ...wideDrawer,
            },
          },
          {
            path: 'road',
            loadComponent: () =>
              import(
                './vicnames/polygon/polygon-search-road-result.component'
              ).then((c) => c.PolygonSearchRoadResultComponent),
            resolve: {
              geoJson: polygonSearchResolver,
            },
            data: {
              title: 'Polygon search results',
              drawerBack: 'vicnames',
              bottomSheetBack: 'vicnames',
              ...wideDrawer,
            },
          },
        ],
      },
      {
        path: 'feature-search',
        canDeactivate: [
          saveCurrentPath(
            'normalFeatureSearchForm',
            'duplicateFeatureSearchForm',
          ),
        ],
        loadComponent: () =>
          import('./vicnames/feature/feature-search.component').then(
            (c) => c.FeatureSearchComponent,
          ),
        children: [
          {
            path: '',
            redirectTo: 'normal',
            pathMatch: 'full',
          },
          {
            path: 'normal',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    './vicnames/feature/feature-search-normal.component'
                  ).then((c) => c.FeatureSearchNormalComponent),
                data: {
                  title: 'Feature Search',
                  bottomSheetBack: 'vicnames',
                  drawerBack: 'vicnames',
                },
              },
              {
                path: 'results',
                loadComponent: () =>
                  import(
                    './vicnames/feature/feature-search-normal-results.component'
                  ).then((c) => c.FeatureSearchNormalResultsComponent),
                data: {
                  title: 'Feature search results',
                  bottomSheetBack: 'vicnames/feature-search/normal',
                  drawerBack: 'vicnames/feature-search/normal',
                  ...wideDrawer,
                },
              },
            ],
          },
          {
            path: 'duplicates',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    './vicnames/feature/feature-search-duplicates.component'
                  ).then((c) => c.FeatureSearchDuplicatesComponent),
                data: {
                  title: 'Feature Search',
                  bottomSheetBack: 'vicnames',
                  drawerBack: 'vicnames',
                },
              },
              {
                path: 'results',
                loadComponent: () =>
                  import(
                    './vicnames/feature/feature-search-duplicates-result.component'
                  ).then((c) => c.FeatureSearchDuplicatesResultComponent),
                data: {
                  title: 'Duplicate feature results',
                  bottomSheetBack: 'vicnames/feature-search/duplicates',
                  drawerBack: 'vicnames/feature-search/duplicates',
                  ...wideDrawer,
                },
              },
            ],
          },
        ],
      },
      {
        path: 'road-search',
        canDeactivate: [
          saveCurrentPath('normalRoadSearchForm', 'duplicateRoadSearchForm'),
        ],
        loadComponent: () =>
          import('./vicnames/road/road-search.component').then(
            (c) => c.RoadSearchComponent,
          ),
        children: [
          {
            path: '',
            redirectTo: 'normal',
            pathMatch: 'full',
          },
          {
            path: 'normal',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./vicnames/road/road-search-normal.component').then(
                    (c) => c.RoadSearchNormalComponent,
                  ),
                data: {
                  title: 'Road Search',
                  bottomSheetBack: 'vicnames',
                  drawerBack: 'vicnames',
                },
              },
              {
                path: 'results',
                loadComponent: () =>
                  import(
                    './vicnames/road/road-search-normal-results.component'
                  ).then((c) => c.RoadSearchNormalResultsComponent),
                data: {
                  title: 'Road search results',
                  bottomSheetBack: 'vicnames/road-search/normal',
                  drawerBack: 'vicnames/road-search/normal',
                  ...wideDrawer,
                },
              },
            ],
          },
          {
            path: 'duplicates',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    './vicnames/road/road-search-duplicates.component'
                  ).then((c) => c.RoadSearchDuplicatesComponent),
                data: {
                  title: 'Road Search',
                  bottomSheetBack: 'vicnames',
                  drawerBack: 'vicnames',
                },
              },
              {
                path: 'results',
                loadComponent: () =>
                  import(
                    './vicnames/road/road-search-duplicates-results.component'
                  ).then((c) => c.RoadSearchDuplicatesResultsComponent),
                data: {
                  title: 'Duplicate road results',
                  bottomSheetBack: 'vicnames/road-search/duplicates',
                  drawerBack: 'vicnames/road-search/duplicates',
                  ...wideDrawer,
                },
              },
            ],
          },
        ],
      },
      {
        path: 'history-search',
        canActivate: [
          featureFlagGuard(IFeatureFlagKeys.enableHistoricalInformationSearch),
        ],
        canDeactivate: [saveCurrentPath('vicnamesHistorySearchForm')],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./vicnames/history/history-search-form.component').then(
                (c) => c.HistorySearchFormComponent,
              ),
            data: {
              title: 'Historical Info Search',
              bottomSheetBack: 'vicnames',
              drawerBack: 'vicnames',
            },
          },
          {
            path: 'results',
            loadComponent: () =>
              import(
                './vicnames/history/history-search-results.component'
              ).then((c) => c.HistorySearchResultsComponent),
            data: {
              title: 'Historical Information Search Results',
              bottomSheetBack: 'vicnames/history-search',
              drawerBack: 'vicnames/history-search',
              ...wideDrawer,
            },
          },
        ],
      },
      {
        path: 'data-download',
        canActivate: [
          featureFlagGuard(IFeatureFlagKeys.enableVicnamesDataDownload),
        ],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./vicnames/data-download/data-download.component').then(
                (c) => c.DataDownloadComponent,
              ),
            data: {
              title: 'Download VICNAMES data',
              drawerBack: 'vicnames',
              ...wideDrawer,
            },
          },
        ],
      },
      {
        path: 'admin',
        runGuardsAndResolvers: 'always',
        canActivate: [
          userRoleGuard([UserGroup.VICNAMES_ADMIN, UserGroup.LASSI_ADMIN]),
        ],
        canDeactivate: [saveCurrentPath('historySubmissions')],
        children: [
          {
            path: 'custom-radius-list',
            canActivate: [
              featureFlagGuard(IFeatureFlagKeys.enableCustomRadius),
            ],
            loadComponent: () =>
              import(
                './vicnames/custom-radius/custom-radius-list.component'
              ).then((c) => c.CustomRadiusListComponent),
            data: {
              title: 'Custom radius polygons',
              drawerBack: 'vicnames',
            },
          },
          {
            path: 'custom-radius',
            canActivate: [
              featureFlagGuard(IFeatureFlagKeys.enableCustomRadius),
            ],
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    './vicnames/custom-radius/custom-radius.component'
                  ).then((c) => c.CustomRadiusComponent),
                canDeactivate: [unsavedChangesGuard],
                data: {
                  title: 'Create custom radius polygon',
                  drawerBack: 'vicnames/admin/custom-radius-list',
                },
              },
              {
                path: ':id',
                loadComponent: () =>
                  import(
                    './vicnames/custom-radius/custom-radius.component'
                  ).then((c) => c.CustomRadiusComponent),
                canDeactivate: [unsavedChangesGuard],
                data: {
                  title: 'Edit custom radius polygon',
                  drawerBack: 'vicnames/admin/custom-radius-list',
                },
              },
            ],
          },
          {
            path: 'history-submissions',
            loadComponent: () =>
              import(
                './admin/historical-information/pending-hist-info-submissions.component'
              ).then((c) => c.PendingHistInfoSubmissionsComponent),
            data: {
              title: 'Historical Information Submissions',
              bottomSheetBack: 'vicnames',
              drawerBack: 'vicnames',
              ...wideDrawer,
            },
          },
          {
            path: 'manage-ref-tables',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    './vicnames/admin/reference/manage-ref-tables.component'
                  ).then((c) => c.ManageRefTablesComponent),
                data: {
                  title: 'Manage reference tables',
                  bottomSheetBack: 'vicnames',
                  drawerBack: 'vicnames',
                },
              },
              {
                path: 'municipality-list',
                loadComponent: () =>
                  import(
                    './vicnames/admin/reference/municipality/municipality-list.component'
                  ).then((c) => c.MunicipalityListComponent),
                data: {
                  title: 'Municipality List',
                  bottomSheetBack: 'vicnames/admin/manage-ref-tables',
                  drawerBack: 'vicnames/admin/manage-ref-tables',
                  ...wideDrawer,
                },
              },
              {
                path: 'feature-name-status',
                loadComponent: () =>
                  import(
                    './vicnames/admin/reference/feature-name-status/feature-name-status-list.component'
                  ).then((c) => c.FeatureNameStatusListComponent),
                data: {
                  title: 'Feature Name Status',
                  bottomSheetBack: 'vicnames/admin/manage-ref-tables',
                  drawerBack: 'vicnames/admin/manage-ref-tables',
                  ...wideDrawer,
                },
              },
              {
                path: 'traditional-owner-flag-list',
                loadComponent: () =>
                  import(
                    './vicnames/admin/reference/traditional-owner-flag/flag-list.component'
                  ).then((c) => c.FlagListComponent),
                data: {
                  title: 'Traditional Owner Flag List',
                  bottomSheetBack: 'vicnames/admin/manage-ref-tables',
                  drawerBack: 'vicnames/admin/manage-ref-tables',
                  ...wideDrawer,
                },
              },
              {
                path: 'feature-code-list',
                loadComponent: () =>
                  import(
                    './vicnames/admin/reference/feature-code/feature-code-list.component'
                  ).then((c) => c.FeatureCodeListComponent),
                data: {
                  title: 'Feature Code',
                  bottomSheetBack: 'vicnames/admin/manage-ref-tables',
                  drawerBack: 'vicnames/admin/manage-ref-tables',
                  ...wideDrawer,
                },
              },
              {
                path: 'naming-authority-list',
                loadComponent: () =>
                  import(
                    './vicnames/admin/reference/naming-authority/authority-list.component'
                  ).then((c) => c.AuthorityListComponent),
                data: {
                  title: 'Naming Authorities',
                  bottomSheetBack: 'vicnames/admin/manage-ref-tables',
                  drawerBack: 'vicnames/admin/manage-ref-tables',
                  ...wideDrawer,
                },
              },
              {
                path: 'locality-radius-list',
                loadComponent: () =>
                  import(
                    './vicnames/admin/reference/locality-radius/locality-radius-list.component'
                  ).then((c) => c.LocalityRadiusListComponent),
                data: {
                  title: 'Radius for locality',
                  bottomSheetBack: 'vicnames/admin/manage-ref-tables',
                  drawerBack: 'vicnames/admin/manage-ref-tables',
                  ...wideDrawer,
                },
              },
            ],
          },
          {
            path: 'add-feature',
            loadComponent: () =>
              import('./vicnames/admin/feature/add-feature.component').then(
                (c) => c.AddFeatureComponent,
              ),
            data: {
              title: 'Add a feature',
              bottomSheetBack: 'vicnames',
              drawerBack: 'vicnames',
            },
          },
          {
            path: 'compile-reports',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    './vicnames/admin/reports/compile-reports.component'
                  ).then((c) => c.CompileReportsComponent),
                data: {
                  title: 'Compile reports',
                  bottomSheetBack: 'vicnames',
                  drawerBack: 'vicnames',
                },
              },
              {
                path: 'classification-data',
                loadComponent: () =>
                  import(
                    './vicnames/admin/reports/classification/classification-data.component'
                  ).then((c) => c.ClassificationDataComponent),
                data: {
                  title: 'Classification data',
                  drawerBack: 'vicnames/admin/compile-reports',
                  ...wideDrawer,
                },
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: 'smes',
    runGuardsAndResolvers: 'always',
    canDeactivate: [clearSavedPath],
    canActivate: [
      userRoleGuard([
        UserGroup.SMES_USER,
        UserGroup.SMES_ADMIN,
        UserGroup.LASSI_ADMIN,
      ]),
    ],
    children: [
      {
        path: '',
        canActivate: [restoreCurrentPath],
        loadComponent: () =>
          import('./smes/smes-home.component').then((c) => c.SmesHomeComponent),
        data: {
          title: 'Manage Survey Marks',
          bottomSheetBack: '../landing',
        },
      },
      {
        path: 'admin',
        runGuardsAndResolvers: 'always',
        canActivate: [
          userRoleGuard([UserGroup.SMES_ADMIN, UserGroup.LASSI_ADMIN]),
        ],
        children: [
          {
            path: 'download-approvals',
            canActivate: [
              featureFlagGuard(
                IFeatureFlagKeys.enableAdminSurveyMarkManagement,
              ),
            ],
            loadComponent: () =>
              import(
                './smes/admin/approvals/admin-download-approvals.component'
              ).then((c) => c.AdminDownloadApprovalsComponent),
            data: {
              title: 'Download approved survey marks',
              drawerBack: 'smes',
              ...wideDrawer,
            },
          },
          {
            path: 'submissions',
            canActivate: [
              featureFlagGuard(
                IFeatureFlagKeys.enableAdminSurveyMarkManagement,
              ),
            ],
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./smes/admin/review-mark-submissions.component').then(
                    (c) => c.ReviewMarkSubmissionsComponent,
                  ),
                data: {
                  title: 'Review & approve submissions',
                  drawerBack: 'smes',
                  ...wideDrawer,
                },
              },
              {
                path: ':submissionId',
                loadComponent: () =>
                  import('./smes/admin/review-mark-submission.component').then(
                    (c) => c.ReviewMarkSubmissionComponent,
                  ),
                data: {
                  title: 'Review submission',
                  drawerBack: 'smes/admin/submissions',
                },
              },
            ],
          },
          {
            path: 'reserved-marks',
            canActivate: [
              featureFlagGuard(
                IFeatureFlagKeys.enableAdminSurveyMarkManagement,
              ),
            ],
            loadComponent: () =>
              import(
                './smes/admin/reserved-mark/all-reserved-marks.component'
              ).then((c) => c.AllReservedMarksComponent),
            data: {
              title: 'All reserved mark numbers',
              drawerBack: 'smes',
              ...wideDrawer,
            },
          },
          {
            path: 'search',
            canActivate: [
              featureFlagGuard(
                IFeatureFlagKeys.enableAdminSurveyMarkManagement,
              ),
            ],
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./smes/admin/admin-search-marks.component').then(
                    (c) => c.AdminSearchMarksComponent,
                  ),
                data: {
                  title: 'Manage marks',
                  drawerBack: 'smes',
                },
              },
              {
                path: 'result',
                loadComponent: () =>
                  import(
                    './smes/admin/admin-search-marks-results.component'
                  ).then((c) => c.AdminSearchMarksResultsComponent),
                data: {
                  title: 'Manage marks',
                  drawerBack: 'smes/admin/search',
                },
              },
            ],
          },
          {
            path: 'manage',
            canActivate: [
              featureFlagGuard(
                IFeatureFlagKeys.enableAdminSurveyMarkManagement,
              ),
            ],
            children: [
              {
                path: '',
                canDeactivate: [unsavedChangesGuard],
                loadComponent: () =>
                  import('./smes/admin/admin-create-mark.component').then(
                    (c) => c.AdminCreateMarkComponent,
                  ),
                data: {
                  title: 'Add new mark',
                  drawerBack: 'smes/admin/search',
                  ...wideDrawer,
                },
              },
              {
                path: ':mark_id',
                canDeactivate: [unsavedChangesGuard],
                loadComponent: () =>
                  import('./smes/admin/admin-edit-mark.component').then(
                    (c) => c.AdminEditMarkComponent,
                  ),
                data: {
                  title: 'Edit mark',
                  drawerBack: 'smes/admin/search',
                  ...wideDrawer,
                },
              },
            ],
          },
          {
            path: 'annual-statistics-report',
            canActivate: [
              featureFlagGuard(IFeatureFlagKeys.enableAnnualStatistics),
            ],
            loadComponent: () =>
              import(
                './smes/admin/annual-statistics/annual-statistics-report.component'
              ).then((c) => c.AnnualStatisticsReportComponent),
            data: {
              title: 'Annual statistics report',
              drawerBack: 'smes',
              ...wideDrawer,
            },
          },
          {
            path: 'adjustments',
            canActivate: [
              featureFlagGuard(IFeatureFlagKeys.enableBulkAdjustments),
            ],
            loadComponent: () =>
              import('./smes/admin/adjustments/adjustments.component').then(
                (c) => c.AdjustmentsComponent,
              ),
            data: {
              title: 'Upload Adjustments',
              drawerBack: 'smes',
              ...wideDrawer,
            },
          },
        ],
      },
      {
        path: 'mark-reservation',
        loadComponent: () =>
          import('./smes/mark-reservation.component').then(
            (c) => c.MarkReservationComponent,
          ),
        data: {
          title: 'Reserve/release numbers',
          bottomSheetBack: 'smes',
          drawerBack: 'smes',
        },
      },
      {
        path: 'mark-lodge',
        canDeactivate: [saveCurrentPath('newMarkForm')],
        loadComponent: () =>
          import('./smes/mark-lodge/mark-lodge-new.component').then(
            (c) => c.MarkLodgeNewComponent,
          ),
        data: {
          title: 'Lodge new Marks',
          bottomSheetBack: 'smes',
          drawerBack: 'smes',
        },
      },
      {
        path: 'mark-submissions',
        canDeactivate: [saveCurrentPath('editSubmissionForm')],
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './smes/mark-submissions/mark-submission-management.component'
              ).then((c) => c.MarkSubmissionManagementComponent),
            data: {
              title: 'Manage Mark Submissions',
              bottomSheetBack: 'smes',
              drawerBack: 'smes',
            },
          },
          {
            path: ':lodgeStatus/:submissionId',
            loadComponent: () =>
              import(
                './smes/mark-submissions/edit-mark-submission.component'
              ).then((c) => c.EditMarkSubmissionComponent),
            data: {
              title: 'Edit Submission',
              bottomSheetBack: 'smes/mark-submissions',
              drawerBack: 'smes/mark-submissions',
            },
          },
        ],
      },
      {
        path: 'mark-existing',
        canDeactivate: [saveCurrentPath('editMarkForm')],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./smes/existing/mark-search-existing.component').then(
                (c) => c.MarkSearchExistingComponent,
              ),
            data: {
              title: 'Search existing',
              bottomSheetBack: 'smes',
              drawerBack: 'smes',
            },
          },
          {
            path: ':nameOrNineFig',
            canActivate: [isPMMarkGuard],
            resolve: {
              drawerBack: goBackFromEditMark,
              bottomSheetBack: goBackFromEditMark,
            },
            loadComponent: () =>
              import('./smes/existing/mark-edit-existing.component').then(
                (c) => c.MarkEditExistingComponent,
              ),
            data: {
              title: 'Update existing',
            },
          },
        ],
      },
    ],
  },
  {
    path: 'layers',
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            '@lassi/webapp-routes/layer-management/layer-management.component'
          ).then((c) => c.LayerManagementComponent),
        data: {
          title: 'Layers',
          bottomSheetBack: '../landing',
        },
      },
      {
        path: 'layer-properties/:name',
        loadComponent: () =>
          import(
            '@lassi/webapp-routes/layer-management/layer-properties.component'
          ).then((c) => c.LayerPropertiesComponent),
        data: {
          title: 'Layer Properties',
          bottomSheetBack: 'layers',
          drawerBack: 'layers',
        },
      },
      {
        path: 'all-layers',
        loadComponent: () =>
          import('./map/layer-management/all-layers.component').then(
            (c) => c.AllLayersComponent,
          ),
        data: {
          title: 'All Layers',
          bottomSheetBack: 'layers',
          drawerBack: 'layers',
        },
      },
    ],
  },
  {
    path: 'markup',
    loadComponent: () =>
      import('./markup/markup.component').then((c) => c.MarkupComponent),
    data: {
      title: 'Mark up',
    },
  },
  {
    path: 'search',
    canDeactivate: [clearSavedPath],
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            '@lassi/webapp-routes/advanced-search/advanced-search-home.component'
          ).then((c) => c.AdvancedSearchHomeComponent),
        canActivate: [restoreCurrentPath],
        data: {
          bottomSheetBack: '../landing',
          title: 'Advanced Search',
        },
      },
      {
        path: 'address-search',
        canDeactivate: [saveCurrentPath('addrSearchFormData', 'addressPfi')],
        loadComponent: () =>
          import('./advanced-search/address/address-search.component').then(
            (c) => c.AddressSearchComponent,
          ),
        children: [
          {
            path: '',
            redirectTo: 'advanced',
            pathMatch: 'full',
          },
          {
            path: 'advanced',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    '@lassi/webapp-routes/advanced-search/address/address-form-advanced.component'
                  ).then((c) => c.AddressFormAdvancedComponent),
                data: {
                  bottomSheetBack: 'search',
                  title: 'Address Search',
                  drawerBack: 'search',
                },
              },
              {
                path: 'results',
                children: [
                  {
                    path: 'locality',
                    canActivate: [searchAddressForLocalityGuard],
                    loadComponent: () =>
                      import(
                        '@lassi/webapp-routes/advanced-search/address/locality-search-results.component'
                      ).then((c) => c.LocalitySearchResultsComponent),
                    resolve: {
                      formData: advancedAddressSearchFormDataResolver,
                    },
                    data: {
                      bottomSheetBack: 'search/address-search/advanced',
                      title: 'Results',
                      drawerBack: 'search/address-search/advanced',
                    },
                  },
                  {
                    path: 'road',
                    loadComponent: () =>
                      import(
                        '@lassi/webapp-routes/advanced-search/address/road-search-results.component'
                      ).then((c) => c.RoadSearchResultsComponent),
                    data: {
                      bottomSheetBack: 'search/address-search/advanced',
                      title: 'Results',
                      drawerBack: 'search/address-search/advanced',
                    },
                  },
                  {
                    path: 'road-properties',
                    loadComponent: () =>
                      import(
                        '@lassi/webapp-routes/advanced-search/address/road-search-property-results.component'
                      ).then((c) => c.RoadSearchPropertyResultsComponent),
                    data: {
                      bottomSheetBack:
                        'search/address-search/advanced/results/road',
                      title: 'Results',
                      drawerBack: 'search/address-search/advanced/results/road',
                    },
                  },
                  {
                    path: 'address',
                    loadComponent: () =>
                      import(
                        '@lassi/webapp-routes/advanced-search/address/property-results.component'
                      ).then((c) => c.PropertyResultsComponent),
                    data: {
                      bottomSheetBack: 'search/address-search/advanced',
                      title: 'Results',
                      drawerBack: 'search/address-search/advanced',
                    },
                  },
                ],
              },
            ],
          },
          {
            path: 'pfi',
            loadComponent: () =>
              import(
                './advanced-search/address/address-form-pfi.component'
              ).then((c) => c.AddressFormPfiComponent),
            data: {
              bottomSheetBack: 'search',
              title: 'Address Search',
              drawerBack: 'search',
            },
          },
        ],
      },
      {
        path: 'coordinates-search',
        canDeactivate: [saveCurrentPath('searchByCoordinatesFormData')],
        loadComponent: () =>
          import(
            './advanced-search/coordinates/coordinates-search.component'
          ).then((c) => c.CoordinatesSearchComponent),
        data: {
          bottomSheetBack: 'search',
          title: 'Coordinate Search',
          drawerBack: 'search',
        },
      },
      {
        path: 'crown-description-search',
        canDeactivate: [saveCurrentPath('searchByCrownDescriptionFormData')],
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                '@lassi/webapp-routes/advanced-search/crown-description/crown-description-search.component'
              ).then((c) => c.CrownDescriptionSearchComponent),
            data: {
              bottomSheetBack: 'search',
              title: 'Crown Description Search',
              drawerBack: 'search',
            },
          },
          {
            path: 'results',
            loadComponent: () =>
              import(
                '@lassi/webapp-routes/advanced-search/crown-description/crown-description-search-results.component'
              ).then((c) => c.CrownDescriptionSearchResultsComponent),
            data: {
              bottomSheetBack: 'search/crown-description-search',
              title: 'Crown Description Search',
              drawerBack: 'search/crown-description-search',
            },
          },
        ],
      },
      {
        path: 'lot',
        loadComponent: () =>
          import('./advanced-search/lot/lot-search.component').then(
            (c) => c.LotSearchComponent,
          ),
        canDeactivate: [
          saveCurrentPath(
            'searchLotByPlanFormData',
            'searchLotByStreetFormData',
          ),
        ],
        children: [
          {
            path: '',
            redirectTo: 'plan',
            pathMatch: 'full',
          },
          {
            path: 'plan',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    '@lassi/webapp-routes/advanced-search/lot/lot-search-plan.component'
                  ).then((c) => c.LotSearchPlanComponent),
                data: {
                  bottomSheetBack: 'search',
                  title: 'Lot Search',
                  drawerBack: 'search',
                },
              },
              {
                path: 'results',
                loadComponent: () =>
                  import(
                    '@lassi/webapp-routes/advanced-search/lot/lot-search-plan-results.component'
                  ).then((c) => c.LotSearchPlanResultsComponent),
                data: {
                  bottomSheetBack: 'search/lot/plan',
                  title: 'Results',
                  drawerBack: 'search/lot/plan',
                },
              },
            ],
          },
          {
            path: 'street',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    './advanced-search/lot/street/lot-search-street.component'
                  ).then((c) => c.LotSearchStreetComponent),
                data: {
                  bottomSheetBack: 'search',
                  title: 'Lot Search',
                  drawerBack: 'search',
                },
              },
              {
                path: 'results',
                loadComponent: () =>
                  import(
                    './advanced-search/lot/street/lot-search-street-results.component'
                  ).then((c) => c.LotSearchStreetResultsComponent),
                data: {
                  bottomSheetBack: 'search/lot/street',
                  title: 'Results',
                  drawerBack: 'search/lot/street',
                },
              },
            ],
          },
        ],
      },
      {
        path: 'parcel',
        loadComponent: () =>
          import('./advanced-search/parcel/parcel-search.component').then(
            (c) => c.ParcelSearchComponent,
          ),
        canDeactivate: [
          saveCurrentPath('parcelSpi', 'parcelPfi', 'parcelViewPfi'),
        ],
        children: [
          {
            path: '',
            redirectTo: 'spi',
            pathMatch: 'full',
          },
          {
            path: 'spi',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    './advanced-search/parcel/spi/parcel-spi-search.component'
                  ).then((c) => c.ParcelSpiSearchComponent),
                data: {
                  bottomSheetBack: 'search',
                  title: 'Parcel Search',
                  drawerBack: 'search',
                },
              },
              {
                path: 'results',
                loadComponent: () =>
                  import(
                    './advanced-search/parcel/spi/parcel-spi-search-results.component'
                  ).then((c) => c.ParcelSpiSearchResultsComponent),
                data: {
                  bottomSheetBack: 'search/parcel/spi',
                  title: 'Parcel Search',
                  drawerBack: 'search/parcel/spi',
                },
              },
            ],
          },
          {
            path: 'pfi',
            loadComponent: () =>
              import(
                './advanced-search/parcel/pfi/parcel-pfi-search.component'
              ).then((c) => c.ParcelPfiSearchComponent),
            data: {
              bottomSheetBack: 'search',
              title: 'Parcel Search',
              drawerBack: 'search',
            },
          },
          {
            path: 'view-pfi',
            loadComponent: () =>
              import(
                './advanced-search/parcel/view-pfi/parcel-view-pfi-search.component'
              ).then((c) => c.ParcelViewPfiSearchComponent),
            data: {
              bottomSheetBack: 'search',
              title: 'Parcel Search',
              drawerBack: 'search',
            },
          },
        ],
      },
      {
        path: 'property',
        loadComponent: () =>
          import('./advanced-search/property/property-search.component').then(
            (c) => c.PropertySearchComponent,
          ),
        canDeactivate: [
          saveCurrentPath(
            'propertyNumber', // for property search
            'lgaCode',
            'propertyPfi',
            'propertyViewPfi',
          ),
        ],
        children: [
          {
            path: '',
            redirectTo: 'council-no',
            pathMatch: 'full',
          },
          {
            path: 'council-no',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    './advanced-search/property/council-no/property-council-no-search.component'
                  ).then((c) => c.PropertyCouncilNoSearchComponent),
                data: {
                  bottomSheetBack: 'search',
                  title: 'Property Search',
                  drawerBack: 'search',
                },
              },
              {
                path: 'results',
                loadComponent: () =>
                  import(
                    './advanced-search/property/council-no/property-council-no-results.component'
                  ).then((c) => c.PropertyCouncilNoResultsComponent),
                data: {
                  bottomSheetBack: 'search/property/council-no',
                  title: 'Property Search',
                  drawerBack: 'search/property/council-no',
                },
              },
            ],
          },
          {
            path: 'pfi',
            loadComponent: () =>
              import(
                './advanced-search/property/pfi/property-pfi-search.component'
              ).then((c) => c.PropertyPfiSearchComponent),
            data: {
              bottomSheetBack: 'search',
              title: 'Property Search',
              drawerBack: 'search',
            },
          },
          {
            path: 'view-pfi',
            loadComponent: () =>
              import(
                './advanced-search/property/view-pfi/property-view-pfi-search.component'
              ).then((c) => c.PropertyViewPfiSearchComponent),
            data: {
              bottomSheetBack: 'search',
              title: 'Property Search',
              drawerBack: 'search',
            },
          },
        ],
      },
      {
        path: 'survey-label',
        canDeactivate: [saveCurrentPath('searchSurveyLabelFormData')],
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './advanced-search/survey-label/survey-label-search.component'
              ).then((c) => c.SurveyLabelSearchComponent),
            data: {
              bottomSheetBack: 'search',
              title: 'Survey Label Search',
              drawerBack: 'search',
            },
          },
          {
            path: 'results',
            loadComponent: () =>
              import(
                './advanced-search/survey-label/survey-label-search-results.component'
              ).then((c) => c.SurveyLabelSearchResultsComponent),
            data: {
              bottomSheetBack: 'search/survey-label',
              title: 'Survey Label Search',
              drawerBack: 'search/survey-label',
            },
          },
        ],
      },
      {
        path: 'survey-mark',
        canDeactivate: [saveCurrentPath('searchSurveyMarkFormData')],
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './advanced-search/survey-mark/survey-mark-search.component'
              ).then((c) => c.SurveyMarkSearchComponent),
            data: {
              bottomSheetBack: 'search',
              title: 'Survey Mark Search',
              drawerBack: 'search',
            },
          },
          {
            path: 'results',
            loadComponent: () =>
              import(
                './advanced-search/survey-mark/survey-mark-search-results.component'
              ).then((c) => c.SurveyMarkSearchResultsComponent),
            data: {
              bottomSheetBack: 'search/survey-mark',
              title: 'Survey Mark Search',
              drawerBack: 'search/survey-mark',
            },
          },
        ],
      },
    ],
  },
];

export const appRoutes: Route[] = [
  {
    path: 'map',
    canActivate: [baseLayerGuard, presetGuard],
    providers: [TourMatMenuModule],
    loadComponent: () =>
      import('./map/map-page.component').then((c) => c.MapPageComponent),
    children: [
      {
        path: RouteUtilsService.MAP_MENU_ROOT_PATH,
        outlet: RouteUtilsService.MAP_MENU_OUTLET_NAME,
        loadComponent: () =>
          import('./map/bottom-sheet-drawer-routing.component').then(
            (c) => c.BottomSheetDrawerRoutingComponent,
          ),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./map/bottom-sheet-nav/landing.component').then(
                (c) => c.LandingComponent,
              ),
          },
          ...commonRoutes,
          {
            path: 'identify',
            runGuardsAndResolvers: 'paramsOrQueryParamsChange',
            children: [
              {
                path: `${IdentifyFeatureType.SurveyMark}/:id`,
                data: {
                  title: 'Survey Mark',
                },
                loadComponent: () =>
                  import(
                    './identify/survey-mark/identify-survey-mark.component'
                  ).then((c) => c.IdentifySurveyMarkComponent),
              },
              {
                path: `${IdentifyFeatureType.SurveyLabel}/:id`,
                data: {
                  title: 'Survey Label',
                },
                loadComponent: () =>
                  import(
                    './identify/survey-label/identify-survey-label.component'
                  ).then((c) => c.IdentifySurveyLabelComponent),
              },
              {
                path: `${IdentifyFeatureType.Feature}/:id`,
                data: {
                  title: 'Feature',
                },
                loadComponent: () =>
                  import('./identify/feature/identify-feature.component').then(
                    (c) => c.IdentifyFeatureComponent,
                  ),
              },
              {
                path: `${IdentifyFeatureType.Road}/:id`,
                data: {
                  title: 'Road',
                },
                loadComponent: () =>
                  import('./identify/road/identify-road.component').then(
                    (c) => c.IdentifyRoadComponent,
                  ),
              },
              {
                path: `${IdentifyFeatureType.Parcel}/:vPfis`,
                loadComponent: () =>
                  import(
                    './identify/property/identify-property.component'
                  ).then((c) => c.IdentifyPropertyComponent),
              },
            ],
          },
          {
            path: 'edit-historical-info/:histInfoId',
            runGuardsAndResolvers: 'always',
            canActivate: [
              userRoleGuard([UserGroup.VICNAMES_ADMIN, UserGroup.LASSI_ADMIN]),
            ],
            resolve: {
              drawerBack: (route: ActivatedRouteSnapshot) => {
                return `identify/${route.queryParamMap.get('featureOrRoad')}/${route.queryParamMap.get('featureOrRoadId')}`;
              },
            },
            loadComponent: () =>
              import(
                './admin/historical-information/edit-hist-info-submission.component'
              ).then((c) => c.EditHistInfoSubmissionComponent),
            data: {
              title: 'Edit Historical Information',
              ...wideDrawer,
            },
          },
          {
            path: 'edit-feature/:featureId',
            runGuardsAndResolvers: 'always',
            canActivate: [
              featureFlagGuard(IFeatureFlagKeys.enableAddFeatureNames),
              userRoleGuard([UserGroup.VICNAMES_ADMIN, UserGroup.LASSI_ADMIN]),
            ],
            resolve: {
              drawerBack: (route: ActivatedRouteSnapshot) => {
                return `identify/feature/${route.paramMap.get('featureId')}`;
              },
            },
            loadComponent: () =>
              import('./vicnames/admin/feature/edit-feature.component').then(
                (c) => c.EditFeatureComponent,
              ),
            data: {
              title: 'Edit feature',
            },
          },
        ],
      },
    ],
  },
  {
    path: 'identify/:type/:id',
    redirectTo: identifyRedirect,
  },
  {
    path: 'mark-submission/:lodgeStatus/:submissionId',
    redirectTo: smesSubmissionRedirectFn,
  },
  {
    path: 'profile',
    canActivate: [authGuard],
    runGuardsAndResolvers: 'always',
    loadComponent: () =>
      import('./profile/profile.component').then((c) => c.ProfileComponent),
  },
  {
    path: 'admin',
    canActivate: [
      userRoleGuard(AdminRoles),
      featureFlagGuard(IFeatureFlagKeys.enableAdminDashboard),
    ],
    runGuardsAndResolvers: 'always',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./admin/dashboard.component').then(
            (c) => c.DashboardComponent,
          ),
      },
      {
        path: 'profile/:userId',
        loadComponent: () =>
          import('./admin/profile.component').then((c) => c.ProfileComponent),
      },
    ],
  },
  {
    path: 'register',
    redirectTo: authStepRedirectFn(AuthSteps.Signup),
  },
  {
    path: 'reset-password',
    redirectTo: authStepRedirectFn(AuthSteps.ResetPassword),
  },
  {
    path: 'login',
    canActivate: [nonLoggedInGuard],
    loadComponent: () =>
      import('./auth/auth.component').then((c) => c.AuthComponent),
  },
  {
    path: 'logout',
    redirectTo: logoutRedirectFn,
  },
  {
    path: 'landing',
    loadComponent: () =>
      import('./landing/landing-page.component').then(
        (c) => c.LandingPageComponent,
      ),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./landing/enquiry-options-container.component').then(
            (c) => c.EnquiryOptionsContainerComponent,
          ),
      },
      {
        path: 'enquiry',
        children: [...enquiryOptions, customEnquiryOption].map(
          (enquiryOption) => ({
            path: enquiryOption.id,
            data: {
              enquiryOption,
            },
            loadComponent: () =>
              import('./landing/set-map-container.component').then(
                (c) => c.SetMapContainerComponent,
              ),
          }),
        ),
      },
    ],
  },
  {
    path: 'help',
    canActivate: [featureFlagGuard(IFeatureFlagKeys.enableHelpPage)],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./help/help-page.component').then((c) => c.HelpPageComponent),
      },
      {
        path: 'news',
        canActivate: [
          featureFlagGuard(IFeatureFlagKeys.enableBroadcastMessage),
        ],
        loadComponent: () =>
          import('./broadcast-message/hist-broadcast-msg-page.component').then(
            (c) => c.HistBroadcastMsgPageComponent,
          ),
      },
      {
        path: 'contact',
        canActivate: [featureFlagGuard(IFeatureFlagKeys.enableContactPage)],
        loadComponent: () =>
          import('./help/contact-page.component').then(
            (c) => c.ContactPageComponent,
          ),
      },
    ],
  },
  {
    path: '**',
    redirectTo: 'landing',
  },
];
